'use client';

import { useState } from 'react';

import { useI18n } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';

import { Avatar, Button, Icon, Link } from '@fshr/components';

import MobileNavLinks, { MobileNavigationLink } from './mobile-nav-links';
import { getConfig } from '@fshr/shared-react';

interface MobileProfileNavigationProps {
	isPersisted?: boolean;
	isFullyAuthenticated: boolean;
	avatar?: string;
	name?: string;
	initials?: string;
	hasUpcomingTrips?: boolean;
	hasMembership?: boolean;
	hasOwnedProperties?: boolean;
	isEmployee?: boolean;
	close?: () => void;
}

export default function MobileProfileNavigation({
	isPersisted = false,
	isFullyAuthenticated,
	avatar,
	name = '',
	initials,
	hasMembership = false,
	hasOwnedProperties = false,
	isEmployee = false,
	close,
}: MobileProfileNavigationProps) {
	const { t, localeLink } = useI18n();
	const { FSHR_PUBLIC_RESERVATIONS_BASE_URL } = getConfig();
	const shortenedFirstName = name.length > 8 ? `${name.slice(0, 8)}...` : name;
	const [isOpen, setIsOpen] = useState(true);

	const links = [
		{ text: t('Profile'), link: localeLink('/profile/'), requiresFullAuthentication: false },

		{
			text: t('My Stays'),
			link: localeLink(`/profile/my-stays/`),
			requiresFullAuthentication: false,
			showActive: (pathname: string) => pathname.includes('/profile/my-stays/'),
		},

		hasOwnedProperties && {
			text: t('My Residences'),
			link: `${FSHR_PUBLIC_RESERVATIONS_BASE_URL}${localeLink('/living')}`,
			requiresFullAuthentication: true,
		},

		hasMembership && {
			text: t('Memberships'),
			link: localeLink(`/profile/memberships`),
			requiresFullAuthentication: false,
			showActive: (pathname: string) => pathname.includes('/profile/memberships/'),
		},

		{
			text: t('Account & Preferences'),
			link: localeLink(`/profile/account/info`),
			requiresFullAuthentication: false,
			showActive: (pathname: string) => pathname.includes('/profile/account/'),
		},

		isEmployee && {
			text: t('Employee Travel'),
			link: `${FSHR_PUBLIC_RESERVATIONS_BASE_URL}${localeLink('/profile/employee')}`,
			requiresFullAuthentication: true,
		},
	].filter(Boolean) as MobileNavigationLink[];

	return (
		<div className="border-b border-neutral-300 mx-[35px] flex flex-col gap-[35px]">
			<div className="flex flex-row gap-150 items-center">
				<div className="grow-0 shrink--0">
					<Avatar
						avatar={avatar}
						initials={initials}
						className="h-[46px] w-[46px] md:h-[46px] md:w-[46px] text-[20px] md:text-[20px] pt-[5px] pl-[2px]"
					/>
				</div>

				<Link
					intent="unstyled"
					href="/profile"
					className="uppercase font-sans font-bold text-[12px] tracking-[3px]"
				>
					{shortenedFirstName}
					{isPersisted && (
						<span className="mx-060 relative px-100">
							<Icon
								icon="lock"
								className="inline text-[18px] leading-none absolute top-1/2 left-0 -translate-y-1/2"
							/>
						</span>
					)}
				</Link>

				<Button
					intent="unstyled"
					onClick={() => setIsOpen(!isOpen)}
					className="ml-auto h-200 w-200 text-[20px] flex items-center justify-center"
				>
					<Icon icon={isOpen ? 'less_sign' : 'plus_sign'} className="text-[16px]" />
				</Button>
			</div>

			<MobileNavLinks
				links={links}
				isFullyAuthenticated={isFullyAuthenticated}
				close={close}
				className={cn('-mx-[35px] overflow-hidden grid grid-rows-[0fr] transition-all', {
					'grid-rows-[1fr]': isOpen,
				})}
			/>
		</div>
	);
}
